import { useReducer, useCallback } from 'react'
import { useRestHeaders } from './useRestHeaders'

type TState = {
  loading: boolean
  error: string | null
  reportName: string | null
  reportURL: string | null
}

enum ActionType {
  SetData,
  SetError,
  Reset,
}

type TAction = {
  type: ActionType
  payload?: Partial<TState>
}

const initialState = {
  loading: false,
  error: null,
  reportName: null,
  reportURL: null,
}

function reducer(state: TState, action: TAction): TState {
  switch (action.type) {
    case ActionType.SetData:
      return {
        ...state,
        ...action.payload,
      }
    case ActionType.SetError:
      return {
        ...state,
        loading: false,
        error: action.payload?.error as string,
      }
    case ActionType.Reset:
    default:
      return initialState
  }
}

type FetchDocumentProps = {
  url: string
  name?: string
}

type PresignedUrlPayload = {
  url?: string
  filename?: string
}

const useDocumentFetcher = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { loading, error, reportName, reportURL } = state
  const { getHeaders } = useRestHeaders()

  const fetchDocument = async ({ url, name = '' }: FetchDocumentProps) => {
    const headers = await getHeaders()
    const requestOptions = {
      method: 'GET',
      headers,
    }

    dispatch({
      type: ActionType.SetData,
      payload: { reportName: name, loading: true },
    })

    try {
      const response = await fetch(url, requestOptions)
      if (!response.ok) throw new Error(`${response.status} - ${response.statusText}`)

      // Binary reports (e.g. xlsx) come back as a JSON payload carrying a presigned
      // S3 URL rather than the file bytes. When we detect that payload, fetch the URL
      // in a *separate* request with no custom headers: if we let the original fetch
      // follow a redirect instead, the browser resends this request's own auth headers
      // (Authorization, CF-Access-Client-*) to S3, which collides with the presigned
      // URL's own query-string signature and S3 responds with
      // `400 InvalidArgument: Only one auth mechanism allowed`. All other responses
      // (CSV, raw binary) are treated as the file body directly.
      const contentType = response.headers.get('content-type') || ''
      let blob: Blob
      if (contentType.includes('application/json')) {
        // Clone before reading so we can still fall back to the raw body if the JSON
        // is not a presigned-URL envelope.
        const payload = (await response.clone().json()) as PresignedUrlPayload
        if (payload?.url) {
          const presignedResponse = await fetch(payload.url)
          if (!presignedResponse.ok) {
            throw new Error(
              `${presignedResponse.status} - ${presignedResponse.statusText}`,
            )
          }
          blob = await presignedResponse.blob()
        } else {
          blob = await response.blob()
        }
      } else {
        blob = await response.blob()
      }

      const objectURL = URL.createObjectURL(blob)

      dispatch({
        type: ActionType.SetData,
        payload: { reportURL: objectURL },
      })
    } catch (err) {
      let errorMessage = 'An error occured while downloading report.'
      if (err instanceof Error) {
        errorMessage = err.message
      } else if (typeof err === 'string') {
        errorMessage = err
      }

      dispatch({
        type: ActionType.SetError,
        payload: {
          error: errorMessage,
        },
      })
    }
  }

  const reset = useCallback(() => dispatch({ type: ActionType.Reset }), [])

  const data = { name: reportName, url: reportURL }

  return { fetchDocument, reset, loading, error, data }
}

export { useDocumentFetcher }
